import { useEffect, useState, useContext } from "react";
import toast from "react-hot-toast";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { cabinsApi } from "../../api/cabins";
import { adminApi, type UserCabinAssignment } from "../../api/admin";
import { SeasonContext } from "../../context/SeasonContext";
import type { User, Cabin } from "../../types";

interface Props {
  user: User | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Override-only dialog for a counselor's CampMinder cabin assignments. The
 * sync-owned cabin stays underneath; only `override_cabin_id` is touched on
 * the CabinCounselor row. Scoped to the season's currently selected term so
 * admins are never overriding "all terms" by mistake.
 */
export default function OverrideCounselorCabinDialog({ user, open, onClose, onSuccess }: Props) {
  const { selectedTermId, selectedTerm } = useContext(SeasonContext);
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [assignments, setAssignments] = useState<UserCabinAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const reload = async () => {
    if (!user || selectedTermId == null) return;
    setLoading(true);
    try {
      const [allCabins, rows] = await Promise.all([
        cabinsApi.list({ includeArchived: false }),
        adminApi.userCabinAssignments(user.id, selectedTermId),
      ]);
      setCabins(allCabins);
      setAssignments(rows);
    } catch {
      toast.error("Failed to load cabin assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, selectedTermId]);

  if (!user) return null;

  const save = async (assignment: UserCabinAssignment, value: string) => {
    const overrideCabinId = value === "" ? null : Number(value);
    setSavingId(assignment.id);
    try {
      await cabinsApi.overrideCounselorCabin(
        assignment.cabinId,
        user.id,
        overrideCabinId,
        assignment.termId ?? undefined,
      );
      toast.success(overrideCabinId ? "Override saved" : "Override cleared");
      onSuccess();
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save override");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cabin override — ${user.firstName} ${user.lastName}`}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-xs text-stone-500">
          Showing assignments for{" "}
          <span className="font-medium text-stone-700">
            {selectedTerm?.name ?? "the selected session"}
          </span>
          . Pick a session in the sidebar to switch terms.
        </p>

        {selectedTermId == null ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            Choose a session in the sidebar before setting a counselor's cabin —
            overrides are scoped per term.
          </div>
        ) : loading ? (
          <div className="py-6 text-center text-sm text-stone-400">Loading…</div>
        ) : assignments.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-3 text-sm text-stone-600">
            This counselor has no cabin assignment for {selectedTerm?.name ?? "this term"}.
            Assign them from the cabin's Counselors tab in Manage Cabins.
          </div>
        ) : (
          <ul className="space-y-2">
            {assignments.map((a) => {
              const overrideValue = a.overrideCabinId ? String(a.overrideCabinId) : "";
              const baseName = a.cabin?.name ?? "—";
              const overrideName = a.overrideCabin?.name;
              const overrideOptions = cabins
                .filter((c) => c.id !== a.cabinId)
                .map((c) => ({
                  value: c.id,
                  label: c.cabinGroup ? `${c.cabinGroup.name} — ${c.name}` : c.name,
                }));
              return (
                <li
                  key={a.id}
                  className="rounded-lg border border-stone-200 px-3 py-2.5 space-y-2"
                >
                  <div className="text-sm">
                    <span className="text-stone-500">CampMinder cabin:</span>{" "}
                    <span className="font-medium text-stone-800">{baseName}</span>
                    {overrideName && (
                      <span className="ml-1 text-amber-600 font-medium">
                        → {overrideName} (override)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      options={overrideOptions}
                      placeholder="No override (use cabin above)"
                      value={overrideValue}
                      onChange={(e) => save(a, e.target.value)}
                      disabled={savingId === a.id}
                    />
                    {a.overrideCabinId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => save(a, "")}
                        loading={savingId === a.id}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
